import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { useAppSelector } from '../../redux/store/hooks';
import {
  useDashboardKpis,
  useRevenueTrend,
  useRecentActivity,
  useIncomeExpenseTrend,
} from '../../services/httpServices/queries';
import {
  Users,
  Calendar,
  Activity,
  CreditCard,
  Bed,
  TrendingUp,
  Clock,
  Loader2,
  AlertCircle,
  Stethoscope,
  FlaskConical,
  Scan,
  Pill,
  Package,
  Briefcase,
  FileText,
  CheckCircle,
  Clock3,
  FlaskConicalOff,
  Wallet,
  Receipt,
  RefreshCw,
  Trash2,
  Undo2,
  UserPlus,
  BarChart3,
  ChevronRight,
  ArrowLeft,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { usePermissions } from '../../hooks/usePermissions';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { IncomeExpenseDetailsPanel } from '../../components/ui/IncomeExpenseDetailsPanel';
import type { TrendGranularity } from '../../types/dashboard';

/** Income vs expense chart palette (chart-safe hex — SVG can't read CSS vars). */
const INCOME_COLOR = '#14b8a6';
const INCOME_COLOR_DARK = '#0f766e';
const EXPENSE_COLOR = '#f59e0b';
const EXPENSE_COLOR_DARK = '#ef4444';

const GRANULARITIES: { value: TrendGranularity; label: string; hint: string }[] = [
  { value: 'day', label: 'Daily', hint: 'Last 14 days' },
  { value: 'week', label: 'Weekly', hint: 'Last 12 weeks' },
  { value: 'month', label: 'Monthly', hint: 'Last 12 months' },
  { value: 'year', label: 'Yearly', hint: 'Last 5 years' },
];

/** Month options (value = 1-12) and how many past years the trend filter offers. */
const MONTH_OPTIONS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const TREND_YEAR_SPAN = 6;

function formatCurrency(value: number): string {
  return `৳${Math.round(value).toLocaleString()}`;
}

function compactCurrency(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `৳${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `৳${Math.round(value / 1_000)}k`;
  return `৳${Math.round(value)}`;
}

function formatPeriodLabel(period: string, granularity: TrendGranularity): string {
  const date = parseISO(period);
  if (Number.isNaN(date.getTime())) return period;
  switch (granularity) {
    case 'year':
      return format(date, 'yyyy');
    case 'month':
      return format(date, 'M/yyyy');
    default:
      return format(date, 'd/M');
  }
}

/** Icon + tone for each recent-activity record type. */
function activityVisual(type: string): { icon: React.ElementType; className: string } {
  switch (type) {
    case 'patient_registration':
      return { icon: UserPlus, className: 'bg-primary/10 text-primary' };
    case 'visit':
      return { icon: Stethoscope, className: 'bg-secondary/10 text-secondary' };
    case 'payment':
      return { icon: Receipt, className: 'bg-success/10 text-success' };
    case 'appointment':
      return { icon: Calendar, className: 'bg-primary/10 text-primary' };
    default:
      return { icon: Clock, className: 'bg-primary/10 text-primary' };
  }
}


interface KpiItem {
  label: string;
  key: string;
  icon: React.ElementType;
  route?: string;
  color: string;
  prefix?: string;
  suffix?: string;
}

interface QuickAction {
  label: string;
  route?: string;
  icon: React.ElementType;
}

interface DashboardConfig {
  title: string;
  kpis: KpiItem[];
  quickActions: QuickAction[];
}

/**
 * KPI tiles that expose the Risk Fund ledger. They are shown only to roles that
 * can open the ledger (`/billing/risk-fund`), mirroring the explicit
 * `billing:risk-fund:*` grant — a built-in finance role never sees write-off
 * totals unless the permission was granted on purpose.
 */
const RISK_FUND_KPI_KEYS = new Set<string>([
  'riskFund',
  'riskFundOpd',
  'riskFundIpd',
]);

/**
 * Tailwind classes that fit N KPI tiles on one line on wide screens. Written
 * out literally (not composed at runtime) so Tailwind can see them.
 */
const KPI_GRID_COLUMNS: Record<number, string> = {
  1: 'xl:grid-cols-1',
  2: 'xl:grid-cols-2',
  3: 'xl:grid-cols-3',
  4: 'xl:grid-cols-4',
  5: 'xl:grid-cols-5',
  6: 'xl:grid-cols-6',
};

function getRoleDashboard(role: string): DashboardConfig {
  switch (role?.toUpperCase()) {
    case 'ADMIN':
    case 'SUPER_ADMIN':
      return {
        title: 'Administrator Dashboard',
        kpis: [
          { label: 'Total Patients Today', key: 'totalPatients', icon: Users, route: '/patients', color: 'bg-primary/10 text-primary' },
          { label: 'Collected Today', key: 'todayRevenue', icon: TrendingUp, route: '/billing', color: 'bg-success/10 text-success', prefix: '৳' },
          { label: 'Active IPD Admissions', key: 'activeAdmissions', icon: Bed, route: '/ipd', color: 'bg-primary/10 text-primary' },
          { label: 'Bed Occupancy Rate', key: 'bedOccupancyRate', icon: Bed, route: '/ipd', color: 'bg-warning/10 text-warning', suffix: '%' },
          { label: 'Risk Fund', key: 'riskFund', icon: AlertCircle, route: '/billing/risk-fund', color: 'bg-danger/10 text-danger', prefix: '৳' },
        ],
        quickActions: [
          { label: 'Register Patient', route: '/patients', icon: Users },
          { label: 'Generate Report', route: '/reports', icon: FileText },
          { label: 'Manage Roles', route: '/roles', icon: Users },
          { label: 'System Settings', route: '/settings', icon: Activity },
        ],
      };
    case 'DOCTOR':
    case 'CONSULTANT':
      return {
        title: 'Doctor Dashboard',
        kpis: [
          { label: 'Appointments Today', key: 'patientsInQueue', icon: Users, route: '/appointments', color: 'bg-primary/10 text-primary' },
          { label: 'My IPD Patients', key: 'myIpdPatients', icon: Bed, route: '/ipd', color: 'bg-primary/10 text-primary' },
          { label: 'Appointments This Week', key: 'appointmentsThisWeek', icon: Stethoscope, route: '/appointments', color: 'bg-success/10 text-success' },
          { label: 'Pending Reports', key: 'pendingReports', icon: FileText, color: 'bg-warning/10 text-warning' },
        ],
        quickActions: [
          { label: 'Book Appointment', route: '/appointments', icon: Calendar },
          { label: 'View IPD Rounds', route: '/ipd', icon: Bed },
          { label: 'Pharmacy Sales', route: '/pharmacy', icon: Pill },
          { label: 'My Schedule', route: '/appointments', icon: Calendar },
        ],
      };
    case 'NURSE':
    case 'HEAD_NURSE':
      return {
        title: 'Nurse Dashboard',
        kpis: [
          { label: 'Assigned Patients', key: 'assignedPatients', icon: Bed, route: '/ipd', color: 'bg-primary/10 text-primary' },
          { label: 'Vitals Due Now', key: 'vitalsDue', icon: Activity, route: '/ipd', color: 'bg-danger/10 text-danger' },
          { label: 'Medications Due', key: 'medicationsDue', icon: Pill, route: '/pharmacy', color: 'bg-warning/10 text-warning' },
          { label: 'Beds Occupied', key: 'bedsOccupied', icon: Bed, route: '/ipd', color: 'bg-success/10 text-success' },
        ],
        quickActions: [
          { label: 'Record Vitals', route: '/ipd', icon: Activity },
          { label: 'Administer Medication', route: '/pharmacy', icon: Pill },
          { label: 'Add Nursing Note', route: '/ipd', icon: FileText },
          { label: 'View Duty Roster', route: '/duty-roster', icon: Briefcase },
        ],
      };
    case 'RECEPTIONIST':
      return {
        title: 'Receptionist Dashboard',
        kpis: [
          { label: "Today's Appointments", key: 'todayAppointments', icon: Calendar, route: '/appointments', color: 'bg-primary/10 text-primary' },
          { label: 'Patients Checked In', key: 'patientsCheckedIn', icon: CheckCircle, route: '/appointments', color: 'bg-success/10 text-success' },
          { label: 'New Registrations', key: 'newRegistrations', icon: Users, route: '/patients', color: 'bg-secondary/10 text-secondary' },
          { label: 'No-Show Count', key: 'noShowCount', icon: Clock3, route: '/appointments', color: 'bg-danger/10 text-danger' },
        ],
        quickActions: [
          { label: 'Register New Patient', route: '/patients', icon: Users },
          { label: 'Create Visit', route: '/visits', icon: Stethoscope },
          { label: 'Check In Patient', route: '/appointments', icon: CheckCircle },
          { label: 'Book Appointment', route: '/appointments', icon: Calendar },
        ],
      };
    case 'LAB_TECHNICIAN':
    case 'LAB_MANAGER':
      return {
        title: 'Laboratory Dashboard',
        kpis: [
          { label: 'Pending Collections', key: 'pendingCollections', icon: FlaskConical, color: 'bg-warning/10 text-warning' },
          { label: 'Pending Result Entries', key: 'pendingResults', icon: FlaskConicalOff, color: 'bg-danger/10 text-danger' },
          { label: 'Tests Completed Today', key: 'testsCompleted', icon: CheckCircle, color: 'bg-success/10 text-success' },
          { label: 'Critical Values', key: 'criticalValues', icon: AlertCircle, color: 'bg-danger/10 text-danger' },
        ],
        quickActions: [
          { label: 'View Collection List', icon: FlaskConical },
          { label: 'Enter Results', icon: FileText },
          { label: 'Validate Results', icon: CheckCircle },
          { label: 'Print Barcodes', icon: FileText },
        ],
      };
    case 'RADIOLOGIST':
    case 'RADIOLOGY_TECHNICIAN':
      return {
        title: 'Radiology Dashboard',
        kpis: [
          { label: 'Pending Imaging Orders', key: 'pendingImaging', icon: Scan, color: 'bg-warning/10 text-warning' },
          { label: 'Reports Pending Approval', key: 'pendingReports', icon: FileText, color: 'bg-danger/10 text-danger' },
          { label: 'Studies Completed Today', key: 'studiesCompleted', icon: CheckCircle, color: 'bg-success/10 text-success' },
          { label: 'Scheduled Appointments', key: 'scheduledAppointments', icon: Calendar, color: 'bg-primary/10 text-primary' },
        ],
        quickActions: [
          { label: 'View Orders', icon: Scan },
          { label: 'Capture Imaging', icon: Scan },
          { label: 'Write Report', icon: FileText },
          { label: 'Approve Report', icon: CheckCircle },
        ],
      };
    case 'PHARMACIST':
      return {
        title: 'Pharmacy Dashboard',
        kpis: [
          { label: 'Pending Prescriptions', key: 'pendingPrescriptions', icon: Pill, route: '/pharmacy', color: 'bg-warning/10 text-warning' },
          { label: 'Dispensed Today', key: 'dispensedToday', icon: CheckCircle, route: '/pharmacy', color: 'bg-success/10 text-success' },
          { label: 'Low Stock Items', key: 'lowStockItems', icon: Package, route: '/pharmacy/drugs', color: 'bg-danger/10 text-danger' },
          { label: 'Expiring Items (30d)', key: 'expiringItems', icon: Clock, route: '/pharmacy/drugs', color: 'bg-warning/10 text-warning' },
        ],
        quickActions: [
          { label: 'Dispense Medicines', route: '/pharmacy', icon: Pill },
          { label: 'View Stock', route: '/pharmacy/drugs', icon: Package },
          { label: 'Process Return', route: '/pharmacy', icon: FileText },
        ],
      };
    case 'ACCOUNTANT':
    case 'CASHIER':
      return {
        title: 'Finance Dashboard',
        kpis: [
          { label: 'Collected Today', key: 'todayRevenue', icon: CreditCard, route: '/billing', color: 'bg-success/10 text-success', prefix: '৳' },
          { label: 'Patient Dues', key: 'outstandingPayments', icon: CreditCard, route: '/billing', color: 'bg-danger/10 text-danger', prefix: '৳' },
          { label: 'OPD Risk Fund', key: 'riskFundOpd', icon: AlertCircle, route: '/billing/risk-fund', color: 'bg-warning/10 text-warning', prefix: '৳' },
          { label: 'IPD Risk Fund', key: 'riskFundIpd', icon: AlertCircle, route: '/billing/risk-fund', color: 'bg-danger/10 text-danger', prefix: '৳' },
          { label: 'Cashbook Balance', key: 'cashbookBalance', icon: TrendingUp, route: '/billing', color: 'bg-primary/10 text-primary', prefix: '৳' },
          { label: 'Pending Settlements', key: 'pendingSettlements', icon: Clock, route: '/billing', color: 'bg-warning/10 text-warning' },
        ],
        quickActions: [
          { label: 'Record Payment', route: '/billing', icon: CreditCard },
          { label: 'Generate Invoice', route: '/billing', icon: FileText },
          { label: 'Close Cashbook', route: '/billing', icon: CheckCircle },
          { label: 'View Ledger', route: '/billing', icon: TrendingUp },
        ],
      };
    case 'HR_MANAGER':
      return {
        title: 'HR Dashboard',
        kpis: [
          { label: "Today's Attendance", key: 'todayAttendance', icon: Users, route: '/hr', color: 'bg-success/10 text-success' },
          { label: 'Pending Leave Requests', key: 'pendingLeave', icon: FileText, route: '/hr', color: 'bg-warning/10 text-warning' },
          { label: 'Upcoming Payroll', key: 'upcomingPayroll', icon: CreditCard, route: '/hr', color: 'bg-primary/10 text-primary' },
          { label: 'Total Headcount', key: 'totalHeadcount', icon: Users, route: '/hr', color: 'bg-secondary/10 text-secondary' },
        ],
        quickActions: [
          { label: 'Mark Attendance', route: '/hr', icon: CheckCircle },
          { label: 'Approve Leave', route: '/hr', icon: FileText },
          { label: 'Process Payroll', route: '/hr', icon: CreditCard },
          { label: 'Add Employee', route: '/hr', icon: Users },
        ],
      };
    case 'STORE_MANAGER':
      return {
        title: 'Store Dashboard',
        kpis: [
          { label: 'Low Stock Items', key: 'lowStockItems', icon: Package, route: '/pharmacy/drugs', color: 'bg-danger/10 text-danger' },
          { label: 'Expiring Items (30d)', key: 'expiringItems', icon: Clock, route: '/pharmacy/drugs', color: 'bg-warning/10 text-warning' },
          { label: 'Pending POs', key: 'pendingPOs', icon: FileText, route: '/pharmacy/purchases', color: 'bg-primary/10 text-primary' },
          { label: 'Monthly Consumption', key: 'monthlyConsumption', icon: TrendingUp, route: '/pharmacy/ledger', color: 'bg-secondary/10 text-secondary', prefix: '৳' },
        ],
        quickActions: [
          { label: 'Receive Stock', route: '/pharmacy/receipts', icon: Package },
          { label: 'Issue Stock', route: '/pharmacy/drugs', icon: FileText },
          { label: 'Create PO', route: '/pharmacy/purchases', icon: FileText },
          { label: 'Stock Verification', route: '/pharmacy/ledger', icon: CheckCircle },
        ],
      };
    default:
      return {
        title: 'Dashboard',
        kpis: [
          { label: 'Total Patients', key: 'totalPatients', icon: Users, route: '/patients', color: 'bg-primary/10 text-primary' },
          { label: 'Today Visits', key: 'todayVisits', icon: Activity, route: '/visits', color: 'bg-success/10 text-success' },
          { label: 'Appointments', key: 'todayAppointments', icon: Calendar, route: '/appointments', color: 'bg-secondary/10 text-secondary' },
          { label: 'Unpaid Bills', key: 'pendingBills', icon: CreditCard, route: '/billing', color: 'bg-danger/10 text-danger' },
          { label: 'Active Admissions', key: 'activeAdmissions', icon: Bed, route: '/ipd', color: 'bg-primary/10 text-primary' },
          { label: 'Collected Today', key: 'todayRevenue', icon: TrendingUp, route: '/billing', color: 'bg-success/10 text-success', prefix: '৳' },
        ],
        quickActions: [
          { label: 'Register Patient', route: '/patients', icon: Users },
          { label: 'Book Appointment', route: '/appointments', icon: Calendar },
          { label: 'Record Payment', route: '/billing', icon: CreditCard },
        ],
      };
  }
}

export function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const { hasPermission, hasAccess } = usePermissions();
  const queryClient = useQueryClient();
  const [granularity, setGranularity] = useState<TrendGranularity>('day');
  const [refreshing, setRefreshing] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  /** Which income/expense tile has its "Show details" modal open. */
  const [detailsMode, setDetailsMode] = useState<'income' | 'expense' | null>(null);
  /** Which admin widget tab is shown: charts or the activity feed. */
  const [adminTab, setAdminTab] = useState<'charts' | 'activity'>('charts');
  /** Month (1-12) and year driving the Income Trend chart. Defaults to today. */
  const [trendMonth, setTrendMonth] = useState(() => new Date().getMonth() + 1);
  const [trendYear, setTrendYear] = useState(() => new Date().getFullYear());

  const role = user?.roles?.[0] || '';
  const config = getRoleDashboard(role);

  // `hasAccess` is the exact ledger gate: built-in roles only when their route
  // map includes it, custom roles only with an explicit `billing:risk-fund:*`
  // grant. Hide the Risk Fund tiles from everyone else.
  const canViewRiskFund = hasAccess('/billing/risk-fund');
  const visibleKpis = config.kpis.filter(
    (kpi) => canViewRiskFund || !RISK_FUND_KPI_KEYS.has(String(kpi.key)),
  );

  /**
   * Wide screens lay every visible KPI out in a single row, so the admin
   * dashboard shows its five tiles side by side instead of wrapping the fifth
   * onto a new line. Narrower breakpoints still wrap as before.
   */
  const kpiGridColumns =
    KPI_GRID_COLUMNS[Math.min(visibleKpis.length, 6)] ?? 'xl:grid-cols-4';
  // Five or more tiles share the row, so give the text a little less room.
  const compactKpis = visibleKpis.length >= 5;

  // Recent Activity, Income Trend and the Income vs Expense comparison expose
  // hospital-wide patient and financial data, so they are restricted to super
  // admins / admins (or a custom role granted `dashboard:admin`). Mirrors the
  // backend `@Permissions('dashboard:admin')`.
  const canViewAdminWidgets = hasPermission('dashboard:admin');
  /**
   * Month/year filtering on the Income Trend is reserved for admin and
   * super_admin, plus custom roles holding `billing:history:full`. Other roles
   * that were granted `dashboard:admin` still see the chart, limited to the
   * last seven days (the API clamps the range too).
   */
  const canFilterIncomeTrend = hasPermission('billing:history:full');

  /** Inclusive first/last day of the selected month for the Income Trend. */
  const trendRange = useMemo(() => {
    if (!canFilterIncomeTrend) return undefined;
    const monthKey = `${trendYear}-${String(trendMonth).padStart(2, '0')}`;
    const lastDay = new Date(trendYear, trendMonth, 0).getDate();
    return {
      dateFrom: `${monthKey}-01`,
      dateTo: `${monthKey}-${String(lastDay).padStart(2, '0')}`,
    };
  }, [canFilterIncomeTrend, trendYear, trendMonth]);

  const trendYearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: TREND_YEAR_SPAN }, (_, index) => currentYear - index);
  }, []);

  const {
    data: kpisData,
    isLoading: kpisLoading,
    error: kpisError,
  } = useDashboardKpis(role);

  const { data: trendData } = useRevenueTrend(7, canViewAdminWidgets, trendRange);
  const { data: activityData } = useRecentActivity(10, canViewAdminWidgets);
  const { data: incomeExpenseData, isLoading: incomeExpenseLoading } =
    useIncomeExpenseTrend(granularity, canViewAdminWidgets);

  const kpis = kpisData?.data;
  const trend = trendData?.data?.trend || [];
  const incomeExpense = incomeExpenseData?.data;
  const totals = incomeExpense?.totals;

  // "Clear" only hides activity already seen on this device; it never deletes
  // records. The cutoff is stored per user so freshly generated activity keeps
  // appearing after a clear.
  const clearedAtKey = `dashboard:recent-activity:cleared-at:${user?.id ?? 'anon'}`;
  const [clearedAt, setClearedAt] = useState<number>(() => {
    const raw = Number(localStorage.getItem(clearedAtKey));
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
  });

  const visibleActivities = useMemo(
    () =>
      (activityData?.data || []).filter(
        (a) => new Date(a.timestamp).getTime() > clearedAt,
      ),
    [activityData?.data, clearedAt],
  );

  const incomeExpenseChart = (incomeExpense?.trend || []).map((point) => ({
    label: formatPeriodLabel(point.period, incomeExpense?.granularity || granularity),
    income: Math.round(point.income),
    expense: Math.round(point.expense),
  }));
  const activeGranularity = GRANULARITIES.find((g) => g.value === granularity);
  const netValue = totals?.net ?? 0;

  const breakdown = incomeExpense?.breakdown;
  const breakdownPeriodLabel = useMemo(() => {
    if (!incomeExpense?.trend?.length) return activeGranularity?.hint;
    const first = formatPeriodLabel(incomeExpense.trend[0].period, incomeExpense.granularity);
    const last = formatPeriodLabel(
      incomeExpense.trend[incomeExpense.trend.length - 1].period,
      incomeExpense.granularity,
    );
    const range = first === last ? first : `${first} – ${last}`;
    return activeGranularity ? `${activeGranularity.hint} · ${range}` : range;
  }, [incomeExpense, activeGranularity]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    } finally {
      setRefreshing(false);
    }
  };

  const handleClearActivity = () => {
    const now = Date.now();
    setClearedAt(now);
    localStorage.setItem(clearedAtKey, String(now));
    setConfirmClearOpen(false);
    toast.info('Recent activity cleared');
  };

  const handleRestoreActivity = () => {
    setClearedAt(0);
    localStorage.removeItem(clearedAtKey);
    toast.success('Recent activity restored');
  };

  if (kpisLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (kpisError) {
    return (
      <div className="flex items-center justify-center h-64 text-danger gap-2">
        <AlertCircle size={20} />
        <span>Failed to load dashboard data</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl border border-border bg-surface p-6 shadow-sm">
        <div className="pointer-events-none absolute -right-16 -top-20 h-52 w-52 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 right-28 h-44 w-44 rounded-full bg-secondary/10 blur-3xl" />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
              <Activity size={12} /> {role || 'User'}
            </span>
            <h1 className="mt-2 text-2xl font-bold tracking-tight text-text">{config.title}</h1>
            <p className="mt-1 text-sm text-text-muted">
              Welcome back, <span className="font-medium text-text">{user?.name || 'User'}</span>
              {activeGranularity ? ` · ${activeGranularity.hint}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3.5 py-2 text-sm font-medium text-text transition-colors hover:bg-surface disabled:opacity-60"
          >
            <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 ${kpiGridColumns}`}>
        {visibleKpis.map((stat) => {
          const value = (kpis as any)?.[stat.key];
          const displayValue = value != null
            ? `${stat.prefix || ''}${typeof value === 'number' ? value.toLocaleString() : value}${stat.suffix || ''}`
            : '—';
          return (
            <button
              key={stat.label}
              onClick={() => stat.route && navigate(stat.route)}
              className={`group relative overflow-hidden rounded-xl border border-border bg-surface p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${stat.route ? 'cursor-pointer' : 'cursor-default'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className={`text-sm text-text-muted ${compactKpis ? 'line-clamp-2 leading-snug' : 'truncate'}`}>{stat.label}</p>
                  <p className={`mt-1.5 truncate font-bold tracking-tight text-text ${compactKpis ? 'text-xl 2xl:text-2xl' : 'text-2xl'}`}>{displayValue}</p>
                </div>
                <div className={`shrink-0 rounded-lg transition-transform group-hover:scale-105 ${compactKpis ? 'p-2' : 'p-2.5'} ${stat.color}`}>
                  <stat.icon size={compactKpis ? 18 : 20} />
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        {config.quickActions.map((action) => (
          <button
            key={action.label}
            onClick={() => action.route && navigate(action.route)}
            className={`inline-flex items-center gap-2 rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium text-text transition-colors hover:border-primary/40 hover:bg-primary/5 ${action.route ? 'cursor-pointer' : 'cursor-default'}`}
          >
            <action.icon size={16} className="text-primary" />
            {action.label}
          </button>
        ))}
        {canViewAdminWidgets && (
          <button
            type="button"
            onClick={() => setAdminTab((prev) => (prev === 'activity' ? 'charts' : 'activity'))}
            aria-pressed={adminTab === 'activity'}
            className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
              adminTab === 'activity'
                ? 'border-primary/40 bg-primary/10 text-primary'
                : 'border-border bg-surface text-text hover:border-primary/40 hover:bg-primary/5'
            }`}
          >
            <Activity size={16} className="text-primary" />
            Activity
          </button>
        )}
      </div>

      {canViewAdminWidgets && (
        <>
          {adminTab === 'charts' && (
          <>
          {/* Income vs Expenses */}
          <div className="rounded-xl border border-border bg-surface p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h3 className="flex items-center gap-2 text-lg font-semibold text-text">
                  <BarChart3 size={18} className="text-primary" />
                  Income vs Expenses
                </h3>
                <p className="mt-0.5 text-sm text-text-muted">
                  Compare total income and expenses
                  {activeGranularity ? ` · ${activeGranularity.hint}` : ''}
                </p>
              </div>
              <div className="inline-flex rounded-lg border border-border bg-background p-0.5">
                {GRANULARITIES.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setGranularity(option.value)}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      granularity === option.value
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-text-muted hover:text-text'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Summary tiles */}
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-border bg-background p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium uppercase tracking-wide text-text-muted">Total Bill</p>
                  <span className="rounded-lg bg-success/10 p-1.5 text-success">
                    <TrendingUp size={14} />
                  </span>
                </div>
                <p className="mt-2 text-xl font-bold text-text">{formatCurrency(totals?.income ?? 0)}</p>
                {breakdown && (
                  <button
                    type="button"
                    onClick={() => setDetailsMode((prev) => (prev === 'income' ? null : 'income'))}
                    className="mt-2 inline-flex items-center gap-0.5 text-xs font-medium text-primary transition-colors hover:text-primary-dark"
                    aria-expanded={detailsMode === 'income'}
                    aria-label="Toggle details of total income"
                  >
                    {detailsMode === 'income' ? 'Hide details' : 'Show details'}
                    <ChevronRight
                      size={13}
                      className={detailsMode === 'income' ? 'rotate-90 transition-transform' : 'transition-transform'}
                    />
                  </button>
                )}
              </div>
              <div className="rounded-xl border border-border bg-background p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium uppercase tracking-wide text-text-muted">Total Expenses</p>
                  <span className="rounded-lg bg-warning/10 p-1.5 text-warning">
                    <Wallet size={14} />
                  </span>
                </div>
                <p className="mt-2 text-xl font-bold text-text">{formatCurrency(totals?.expense ?? 0)}</p>
                {breakdown && (
                  <button
                    type="button"
                    onClick={() => setDetailsMode((prev) => (prev === 'expense' ? null : 'expense'))}
                    className="mt-2 inline-flex items-center gap-0.5 text-xs font-medium text-primary transition-colors hover:text-primary-dark"
                    aria-expanded={detailsMode === 'expense'}
                    aria-label="Toggle details of total expenses"
                  >
                    {detailsMode === 'expense' ? 'Hide details' : 'Show details'}
                    <ChevronRight
                      size={13}
                      className={detailsMode === 'expense' ? 'rotate-90 transition-transform' : 'transition-transform'}
                    />
                  </button>
                )}
              </div>
              <div className="rounded-xl border border-border bg-background p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium uppercase tracking-wide text-text-muted">Net Balance</p>
                  <span
                    className={`rounded-lg p-1.5 ${
                      netValue >= 0 ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                    }`}
                  >
                    <Activity size={14} />
                  </span>
                </div>
                <p className={`mt-2 text-xl font-bold ${netValue >= 0 ? 'text-success' : 'text-danger'}`}>
                  {netValue < 0 ? '-' : ''}
                  {formatCurrency(Math.abs(netValue))}
                </p>
              </div>
            </div>

            {detailsMode && breakdown && (
              <IncomeExpenseDetailsPanel
                mode={detailsMode}
                side={detailsMode === 'income' ? breakdown.income : breakdown.expense}
                periodLabel={breakdownPeriodLabel}
                onClose={() => setDetailsMode(null)}
              />
            )}

            <div className="mt-5 h-72">
              {incomeExpenseLoading ? (
                <div className="flex h-full items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : incomeExpenseChart.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-text-muted">
                  No income or expense data for this period.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={incomeExpenseChart}
                    margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                    barGap={6}
                  >
                    <defs>
                      <linearGradient id="incomeBar" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={INCOME_COLOR} stopOpacity={0.95} />
                        <stop offset="100%" stopColor={INCOME_COLOR_DARK} stopOpacity={0.9} />
                      </linearGradient>
                      <linearGradient id="expenseBar" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={EXPENSE_COLOR} stopOpacity={0.95} />
                        <stop offset="100%" stopColor={EXPENSE_COLOR_DARK} stopOpacity={0.9} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 13, fill: 'var(--color-text-muted)' }}
                      axisLine={{ stroke: 'var(--color-border)' }}
                      tickLine={false}
                    />
                    <YAxis
                      tickFormatter={compactCurrency}
                      tick={{ fontSize: 13, fill: 'var(--color-text-muted)' }}
                      axisLine={false}
                      tickLine={false}
                      width={56}
                    />
                    <Tooltip
                      cursor={{ fill: 'var(--color-primary)', opacity: 0.06 }}
                      contentStyle={{
                        background: 'var(--color-surface)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 8,
                        fontSize: 13,
                      }}
                      labelStyle={{ color: 'var(--color-text)', fontWeight: 600 }}
                      formatter={(value: any, name: any) => [formatCurrency(Number(value)), name]}
                    />
                    <Legend wrapperStyle={{ fontSize: 13 }} />
                    <Bar dataKey="income" name="Income" fill="url(#incomeBar)" radius={[4, 4, 0, 0]} maxBarSize={40} />
                    <Bar dataKey="expense" name="Expense" fill="url(#expenseBar)" radius={[4, 4, 0, 0]} maxBarSize={40} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          </>
          )}

          {/* Charts view (shown when the Activity view is off) */}
          {adminTab === 'charts' && (
            <div>
              {/* Income Trend */}
              <div className="rounded-xl border border-border bg-surface p-5 shadow-sm">
                <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="flex items-center gap-2 text-lg font-semibold text-text">
                      <TrendingUp size={18} className="text-primary" />
                      Income Trend
                    </h3>
                    <p className="mt-0.5 text-sm text-text-muted">
                      {canFilterIncomeTrend
                        ? `Daily collections · ${MONTH_OPTIONS[trendMonth - 1]} ${trendYear}`
                        : 'Daily collections · Last 7 days'}
                    </p>
                  </div>
                  {canFilterIncomeTrend && (
                    <div className="flex items-center gap-2">
                      <select
                        aria-label="Income trend month"
                        value={trendMonth}
                        onChange={(event) => setTrendMonth(Number(event.target.value))}
                        className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm font-medium text-text shadow-sm transition-colors hover:border-primary/40 focus:border-primary focus:outline-none"
                      >
                        {MONTH_OPTIONS.map((month, index) => (
                          <option key={month} value={index + 1}>
                            {month}
                          </option>
                        ))}
                      </select>
                      <select
                        aria-label="Income trend year"
                        value={trendYear}
                        onChange={(event) => setTrendYear(Number(event.target.value))}
                        className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm font-medium text-text shadow-sm transition-colors hover:border-primary/40 focus:border-primary focus:outline-none"
                      >
                        {trendYearOptions.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(value: string) =>
                          value ? format(parseISO(value), 'dd MMM') : ''
                        }
                        minTickGap={24}
                        tick={{ fontSize: 13, fill: 'var(--color-text-muted)' }}
                        axisLine={{ stroke: 'var(--color-border)' }}
                        tickLine={false}
                      />
                      <YAxis
                        tickFormatter={compactCurrency}
                        tick={{ fontSize: 13, fill: 'var(--color-text-muted)' }}
                        axisLine={false}
                        tickLine={false}
                        width={56}
                      />
                      <Tooltip
                        contentStyle={{
                          background: 'var(--color-surface)',
                          border: '1px solid var(--color-border)',
                          borderRadius: 8,
                          fontSize: 13,
                        }}
                        labelFormatter={(label: string) =>
                          label ? format(parseISO(label), 'dd MMM yyyy') : ''
                        }
                        formatter={(value: any) => [formatCurrency(Number(value)), 'Income']}
                      />
                      <Line
                        type="monotone"
                        dataKey="revenue"
                        stroke={INCOME_COLOR_DARK}
                        strokeWidth={2}
                        dot={{ fill: INCOME_COLOR_DARK, r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {adminTab === 'activity' && (
            <div className="flex min-h-[30rem] max-h-[calc(100vh-10rem)] flex-col rounded-xl border border-border bg-surface p-5 shadow-sm">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setAdminTab('charts')}
                    aria-label="Back to charts"
                    className="group inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-border bg-surface px-3 text-sm font-medium text-text shadow-sm transition-all hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
                  >
                    <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-0.5" />
                    Back
                  </button>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-text">Recent Activity</h3>
                    <p className="mt-0.5 text-sm text-text-muted">
                      Latest patient, visit, payment and appointment events
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {clearedAt > 0 && (
                    <button
                      type="button"
                      onClick={handleRestoreActivity}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-text-muted transition-colors hover:text-text"
                    >
                      <Undo2 size={14} />
                      Restore
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setConfirmClearOpen(true)}
                    disabled={visibleActivities.length === 0}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-danger transition-colors hover:bg-danger/5 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Trash2 size={14} />
                    Clear
                  </button>
                </div>
              </div>
              <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto pr-1">
                {visibleActivities.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
                    <div className="rounded-full bg-primary/10 p-3 text-primary">
                      <CheckCircle size={20} />
                    </div>
                    <p className="text-sm text-text-muted">
                      {clearedAt > 0
                        ? "You're all caught up — cleared activity is hidden."
                        : 'No recent activity yet.'}
                    </p>
                    {clearedAt > 0 && (
                      <button
                        type="button"
                        onClick={handleRestoreActivity}
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                      >
                        <Undo2 size={14} /> Restore cleared activity
                      </button>
                    )}
                  </div>
                ) : (
                  visibleActivities.map((activity, index) => {
                    const visual = activityVisual(activity.type);
                    const Icon = visual.icon;
                    return (
                      <div
                        key={`${activity.type}-${index}`}
                        className="flex items-start gap-3 rounded-lg p-3 transition-colors hover:bg-background"
                      >
                        <div className={`mt-0.5 rounded-lg p-2 ${visual.className}`}>
                          <Icon size={14} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-text">{activity.description}</p>
                          <p className="mt-0.5 text-xs text-text-muted">
                            {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                            <span className="mx-1.5">·</span>
                            {new Date(activity.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={confirmClearOpen}
        title="Clear recent activity?"
        description="This hides the current activity feed on your dashboard. It does not delete any records, and new activity will keep appearing."
        icon={<Trash2 size={18} />}
        confirmLabel="Clear activity"
        tone="danger"
        onConfirm={handleClearActivity}
        onClose={() => setConfirmClearOpen(false)}
      />

    </div>
  );
}
